export const AUTH_TOKEN = 'authToken';
export const REFRESH_ENDPOINT = 'refreshRememberedSession';

const SESSION_KEY = 'rememberedAuthSession';
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const REMEMBER_DURATION = 30 * 24 * 60 * MINUTE;
const MAX_REFRESH_LEAD = 30 * MINUTE;
const FALLBACK_REFRESH_INTERVAL = 12 * 60 * MINUTE;
export const AUTH_RETRY_DELAY = MINUTE;

let refreshTimer: number | undefined;
let pendingRefresh: Promise<AuthRefreshOutcome> | undefined;

const readSession = (): RememberedAuthSession | undefined => {
  const stored = localStorage.getItem(SESSION_KEY);
  if (!stored) return;

  try {
    const session: RememberedAuthSession = JSON.parse(stored);
    if (session?.id && session.jwt && session.refresh
        && Number.isFinite(session.expiresAt) && Number.isFinite(session.refreshAt)) {
      return session;
    }
  } catch {
    return;
  }
};

export const getRememberedSession = () => {
  const session = readSession();
  return session && session.expiresAt > Date.now() ? session : undefined;
};

export const getToken = () => {
  const session = readSession();
  if (session) return session.expiresAt > Date.now() ? session.jwt : null;
  return localStorage.getItem(AUTH_TOKEN);
};

export const getSessionKey = () => getRememberedSession()?.id ?? getToken();

export const clearAuthSession = () => {
  window.clearTimeout(refreshTimer);
  refreshTimer = undefined;
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(AUTH_TOKEN);
};

export const clearExpiredAuthSession = () => {
  const session = readSession();
  if (session && session.expiresAt <= Date.now()) clearAuthSession();
};

export const setToken = (token: string) => {
  if (!token) return;

  localStorage.setItem(AUTH_TOKEN, token);
  localStorage.removeItem(SESSION_KEY);
  window.clearTimeout(refreshTimer);
  refreshTimer = undefined;
};

const tokenExpiry = (token: string): number | undefined => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return Number.isFinite(exp) ? exp * 1000 : undefined;
  } catch {
    return;
  }
};

const writeSession = (session: RememberedAuthSession) => {
  const now = Date.now();
  const accessExpiry = tokenExpiry(session.jwt) ?? now + FALLBACK_REFRESH_INTERVAL;
  const refreshExpiry = tokenExpiry(session.refresh) ?? now + FALLBACK_REFRESH_INTERVAL;
  const tokenLifetime = Math.min(accessExpiry, refreshExpiry) - now;
  const lead = Math.min(MAX_REFRESH_LEAD, tokenLifetime / 2);
  const refreshAt = now + Math.max(SECOND, tokenLifetime - lead);

  localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, refreshAt }));
};

export const saveSignInSession = ({ jwt, refresh, remember }: SaveSignInSessionInput) => {
  if (!jwt) return;

  if (!remember || !refresh) {
    setToken(jwt);
    return;
  }

  window.clearTimeout(refreshTimer);
  refreshTimer = undefined;
  localStorage.removeItem(AUTH_TOKEN);
  writeSession({
    id: Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-'),
    jwt,
    refresh,
    expiresAt: Date.now() + REMEMBER_DURATION,
    refreshAt: 0,
  });
};

const rotateSession = async ({ session, requestRefresh }: RefreshRememberedSessionInput): Promise<AuthRefreshOutcome> => {
  const current = getRememberedSession();

  if (current?.id !== session.id) return 'changed';
  if (current.refresh !== session.refresh || current.jwt !== session.jwt) return 'refreshed';

  const { data, status } = await requestRefresh(current.refresh);
  const latest = getRememberedSession();

  if (latest?.id !== current.id) return 'changed';
  if (latest.refresh !== current.refresh || latest.jwt !== current.jwt) return 'refreshed';

  const jwt = data?.jwt || data?.access;
  if (typeof jwt === 'string' && jwt && typeof data?.refresh === 'string' && data.refresh) {
    writeSession({ ...latest, jwt, refresh: data.refresh });
    return 'refreshed';
  }

  return status === 400 || status === 401 || status === 403 ? 'rejected' : 'unavailable';
};

export const refreshRememberedSession = async (input: RefreshRememberedSessionInput): Promise<AuthRefreshOutcome> => {
  if (pendingRefresh) {
    const outcome = await pendingRefresh;
    if (getRememberedSession()?.id !== input.session.id) return 'changed';
    if (outcome !== 'changed') return outcome;
  }

  const rotate = () => rotateSession(input);
  // The lock covers rotation and storage so tabs never send the same rotating token concurrently.
  const refresh = (navigator.locks ? navigator.locks.request(SESSION_KEY, rotate) : rotate())
    .catch((): AuthRefreshOutcome => 'unavailable');
  pendingRefresh = refresh;

  try {
    return await refresh;
  } finally {
    if (pendingRefresh === refresh) pendingRefresh = undefined;
  }
};

export const scheduleSessionRefresh = (options: ScheduleSessionRefreshInput) => {
  if (refreshTimer !== undefined || !options.canRefresh()) return;

  const session = getRememberedSession();
  if (!session) return;

  const run = async () => {
    if (!options.canRefresh()) {
      refreshTimer = undefined;
      return;
    }

    const current = readSession();
    if (!current) {
      refreshTimer = undefined;
      return;
    }

    if (current.expiresAt <= Date.now()) {
      refreshTimer = undefined;
      options.onExpired(current.id);
      return;
    }

    const outcome = current.refreshAt <= Date.now()
      ? await refreshRememberedSession({ session: current, requestRefresh: options.requestRefresh })
      : undefined;

    if (refreshTimer !== timer) return;
    refreshTimer = undefined;

    if (outcome === 'rejected' || current.expiresAt <= Date.now()) {
      options.onExpired(current.id);
      return;
    }

    scheduleSessionRefresh({
      ...options,
      retryAt: outcome === 'unavailable' ? Date.now() + AUTH_RETRY_DELAY : undefined,
    });
  };

  const refreshAt = Math.max(session.refreshAt, options.retryAt ?? 0);
  const delay = Math.max(0, Math.min(refreshAt, session.expiresAt) - Date.now());
  const timer = window.setTimeout(run, delay);
  refreshTimer = timer;
};

export const isStoredSession = (id: string) => readSession()?.id === id;
